import React, { useEffect, useState } from "react";
import { apiClient } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Tag, Plus, Trash2, Save } from "lucide-react";

export default function MakesTab() {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", name_ar: "", country: "", models: "" });

  const load = () => apiClient.get("/admin/makes").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      name_ar: form.name_ar,
      country: form.country,
      models: form.models.split(",").map(s => s.trim()).filter(Boolean),
    };
    try {
      await apiClient.post("/admin/makes", payload);
      setForm({ name: "", name_ar: "", country: "", models: "" });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Error");
    }
  };

  const update = async (m, patch) => {
    await apiClient.patch(`/admin/makes/${m.id}`, patch);
    load();
  };

  const del = async (id) => {
    if (!window.confirm(lang === "ar" ? "حذف الماركة؟" : "Delete make?")) return;
    await apiClient.delete(`/admin/makes/${id}`);
    load();
  };

  return (
    <div data-testid="admin-makes" className="space-y-6">
      <h2 className="font-display text-2xl flex items-center gap-2"><Tag className="w-5 h-5 text-[#8a6f2e]" /> {lang === "ar" ? "الماركات والموديلات" : "Makes & models"} ({items.length})</h2>

      <form onSubmit={add} className="surface rounded-xl p-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <input className="input-dark" placeholder={lang === "ar" ? "اسم (EN)" : "Name (EN)"} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="make-name" />
        <input className="input-dark" placeholder={lang === "ar" ? "اسم (AR)" : "Name (AR)"} value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} data-testid="make-name-ar" />
        <input className="input-dark" placeholder={lang === "ar" ? "البلد" : "Country"} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="make-country" />
        <input className="input-dark" placeholder={lang === "ar" ? "موديلات (مفصولة بفاصلة)" : "Models (comma)"} value={form.models} onChange={(e) => setForm({ ...form, models: e.target.value })} data-testid="make-models" />
        <button type="submit" className="btn-primary sm:col-span-2 lg:col-span-4" data-testid="make-add"><Plus className="w-4 h-4" /> {lang === "ar" ? "إضافة" : "Add make"}</button>
      </form>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(m => (
          <MakeCard key={m.id} m={m} lang={lang} onUpdate={update} onDelete={del} />
        ))}
      </div>
    </div>
  );
}

function MakeCard({ m, lang, onUpdate, onDelete }) {
  const [models, setModels] = useState((m.models || []).join(", "));
  const [dirty, setDirty] = useState(false);

  const save = async () => {
    await onUpdate(m, { models: models.split(",").map(s => s.trim()).filter(Boolean) });
    setDirty(false);
  };

  return (
    <div className="surface rounded-xl p-4" data-testid={`make-${m.id}`}>
      <div className="flex justify-between items-start gap-2 mb-2">
        <div>
          <div className="font-display text-base">{m.name} {m.name_ar && <span className="text-[#8a8a8a] text-xs">/ {m.name_ar}</span>}</div>
          <div className="text-xs text-[#6b6b6b]">{m.country || "—"} · {(m.models || []).length} {lang === "ar" ? "موديل" : "models"}</div>
        </div>
        <span className={`pill text-[10px] ${m.active ? "pill-accent" : ""}`}>{m.active ? (lang === "ar" ? "نشطة" : "Active") : (lang === "ar" ? "معطّلة" : "Inactive")}</span>
      </div>
      <textarea
        value={models}
        onChange={(e) => { setModels(e.target.value); setDirty(true); }}
        className="input-dark w-full text-xs min-h-[60px] my-2"
        placeholder={lang === "ar" ? "الموديلات مفصولة بفاصلة" : "Comma-separated models"}
        data-testid={`make-${m.id}-models`}
      />
      <div className="flex gap-2 flex-wrap">
        {dirty && <button onClick={save} className="btn-primary text-xs"><Save className="w-3 h-3" /> {lang === "ar" ? "حفظ" : "Save"}</button>}
        <button onClick={() => onUpdate(m, { active: !m.active })} className="btn-ghost text-xs">{m.active ? (lang === "ar" ? "تعطيل" : "Disable") : (lang === "ar" ? "تفعيل" : "Enable")}</button>
        <button onClick={() => onDelete(m.id)} className="btn-ghost text-xs text-red-700 border-red-300"><Trash2 className="w-3 h-3" /></button>
      </div>
    </div>
  );
}
